import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Map as MapIcon, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full h-10 px-2 border border-stone-300 text-sm";
const cell = "h-9 px-2 border border-stone-300 text-sm w-full";

export default function RanchMapImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ranch, setRanch] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => { setFile(null); setErr(""); setRanch(null); setBlocks([]); };

  const scan = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ranches/scan-map", fd, { timeout: 120000 });
      const r = data.ranch || {};
      setRanch({
        ranch_number: r.ranch_number || "", name: r.name || "", address: r.address || "",
        city: r.city || "", county: r.county || "", state: r.state || "CA", zip: r.zip || "", apn: r.apn || "",
      });
      setBlocks((data.blocks || []).map((b) => ({
        include: true, block: b.block || "", acres: b.acres ?? "", variety: b.variety || "",
        clone: b.clone || "", rootstock: b.rootstock || "",
        vines_per_acre: b.vines_per_acre ?? "", notes: [b.spacing, b.year_planted].filter(Boolean).join(" · "),
      })));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed — try again"); }
    finally { setBusy(false); }
  };

  const setBlock = (i, upd) => setBlocks((s) => s.map((b, k) => (k === i ? { ...b, ...upd } : b)));

  const createAll = async () => {
    const chosen = blocks.filter((b) => b.include && b.block.trim());
    if (!ranch.ranch_number.trim() || !ranch.name.trim()) { setErr("Ranch number and name are required"); return; }
    if (chosen.length === 0) { setErr("Select at least one block"); return; }
    setSaving(true); setErr("");
    try {
      for (const b of chosen) {
        await api.post("/ranches", {
          ranch_number: ranch.ranch_number.trim(), name: ranch.name.trim(), block: b.block.trim(),
          address: ranch.address || null, city: ranch.city || null, county: ranch.county || null,
          state: ranch.state || null, zip: ranch.zip || null, apn: ranch.apn || null,
          acres: b.acres !== "" ? Number(b.acres) : null, variety: b.variety || null,
          clone: b.clone || null, rootstock: b.rootstock || null,
          vines_per_acre: b.vines_per_acre !== "" ? Number(b.vines_per_acre) : null,
          location: b.notes || null, active: true,
        });
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("ranch_number", ranch.ranch_number.trim());
        fd.append("title", `${ranch.name} — imported map`);
        await api.post("/irrigation/maps", fd);
      } catch (e) { /* map archive is best-effort */ }
      toast.success(`${chosen.length} block${chosen.length === 1 ? "" : "s"} created from map`);
      setOpen(false); reset();
      onDone && onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button data-testid="import-map-btn" onClick={() => { reset(); setOpen(true); }}
        className="h-14 px-5 bg-white text-[#1F3D34] font-bold uppercase border border-stone-200 shadow-sm flex items-center gap-2">
        <MapIcon size={16} /> Import from Map
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white w-full max-w-3xl p-5 space-y-4 my-8" data-testid="map-import-modal">
            <div className="flex items-center justify-between">
              <div className="font-bold uppercase tracking-wide flex items-center gap-2"><Sparkles size={16} className="text-[#B08D3E]" /> Import Ranch from Map</div>
              <button data-testid="map-import-close" onClick={() => setOpen(false)} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><X size={15} /></button>
            </div>

            {!ranch && (
              <div className="space-y-3">
                <p className="text-sm text-stone-500">Upload a ranch map (PDF or photo). The AI reads block labels, acreage, varieties, clones, rootstock and spacing from the map and its legend — you review everything before anything is created.</p>
                <input data-testid="map-import-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
                {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="map-import-error">{err}</div>}
                <button data-testid="map-scan-btn" onClick={scan} disabled={!file || busy}
                  className="h-12 px-6 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center gap-2">
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Reading map…</> : "Scan Map"}
                </button>
              </div>
            )}

            {ranch && (
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] font-bold uppercase text-stone-500 mb-2">Ranch Info (from map — edit as needed)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[["ranch_number", "Ranch #"], ["name", "Ranch Name"], ["address", "Address"], ["city", "City"],
                      ["county", "County"], ["state", "State"], ["zip", "Zip"], ["apn", "APN"]].map(([k, label]) => (
                      <div key={k}>
                        <div className="text-[9px] font-bold uppercase text-stone-400">{label}</div>
                        <input data-testid={`mi-${k}`} value={ranch[k]} onChange={(e) => setRanch({ ...ranch, [k]: e.target.value })} className={inp} />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-stone-500 mb-2">Blocks Found ({blocks.filter((b) => b.include).length} selected)</div>
                  <div className="overflow-x-auto border border-stone-200">
                    <table className="w-full text-sm min-w-[820px]">
                      <thead><tr className="bg-stone-100 text-left text-[10px] uppercase">
                        <th className="p-2"></th><th className="p-2">Block</th><th className="p-2">Acres</th>
                        <th className="p-2">Variety</th><th className="p-2">Clone</th><th className="p-2">Rootstock</th>
                        <th className="p-2">Vines/Ac</th><th className="p-2">Notes</th>
                      </tr></thead>
                      <tbody>
                        {blocks.map((b, i) => (
                          <tr key={i} className={`border-t border-stone-200 ${b.include ? "" : "opacity-40"}`} data-testid={`mi-block-row-${i}`}>
                            <td className="p-2"><input data-testid={`mi-include-${i}`} type="checkbox" checked={b.include} onChange={(e) => setBlock(i, { include: e.target.checked })} className="w-4 h-4" /></td>
                            <td className="p-1"><input data-testid={`mi-block-${i}`} value={b.block} onChange={(e) => setBlock(i, { block: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-20"><input data-testid={`mi-acres-${i}`} type="number" step="0.1" value={b.acres} onChange={(e) => setBlock(i, { acres: e.target.value })} className={cell} /></td>
                            <td className="p-1"><input data-testid={`mi-variety-${i}`} value={b.variety} onChange={(e) => setBlock(i, { variety: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-20"><input data-testid={`mi-clone-${i}`} value={b.clone} onChange={(e) => setBlock(i, { clone: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-24"><input data-testid={`mi-rootstock-${i}`} value={b.rootstock} onChange={(e) => setBlock(i, { rootstock: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-24"><input data-testid={`mi-vpa-${i}`} type="number" value={b.vines_per_acre} onChange={(e) => setBlock(i, { vines_per_acre: e.target.value })} className={cell} /></td>
                            <td className="p-1"><input data-testid={`mi-notes-${i}`} value={b.notes} onChange={(e) => setBlock(i, { notes: e.target.value })} className={cell} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="map-import-error">{err}</div>}
                <div className="flex gap-2">
                  <button data-testid="mi-rescan" onClick={reset} className="h-12 px-4 border border-stone-300 font-bold uppercase text-xs">Start Over</button>
                  <button data-testid="mi-create-btn" onClick={createAll} disabled={saving}
                    className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center justify-center gap-2">
                    {saving && <Loader2 size={14} className="animate-spin" />} Create {blocks.filter((b) => b.include).length} Block{blocks.filter((b) => b.include).length === 1 ? "" : "s"} + Save Map
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
